import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  Pagination,
  Tooltip,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  FilterListOff,
  Refresh,
  FilterList,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { productService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS, withOpacity } from '../theme/colors';
import SEOHead from '../components/SEO/SEOHead';
import LazyImage from '../components/UI/LazyImage';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  // Initialize category from ?category= query param if present
  const [category, setCategory] = useState(() => {
    try {
      return new URLSearchParams(location.search).get('category') || '';
    } catch {
      return '';
    }
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const productsPerPage = 9;

  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [stockFilter, setStockFilter] = useState(''); // '' | 'in-stock' | 'out-of-stock'
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  // Mobile filter state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      //console.log('🔍 Fetching products via API...');

      // Use the same API service as Home.js and Admin
      const params = {};
      if (category) {
        //console.log('📂 Filtering by category:', category);
        params.category = category;
      }

      const response = await productService.getProducts(params);
      //console.log('📊 API response:', response);
      
      const productsData = response.data?.products || [];
      //console.log('✅ Products fetched:', productsData.length, productsData);
      setProducts(productsData);
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      //console.log('🏷️ Fetching categories via API...');
      const response = await productService.getCategories();
      //console.log('📊 Categories API response:', response);
      
      const categoriesData = response.data?.categories || [];
      //console.log('✅ Categories fetched:', categoriesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [category]);

  // Keep category state in sync if the URL query param changes while on this page
  useEffect(() => {
    const paramCategory = new URLSearchParams(location.search).get('category') || '';
    if (paramCategory !== category) {
      setCategory(paramCategory);
    }
  }, [location.search]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const textMatch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!textMatch) return false;
      if (stockFilter === 'in-stock' && !product.inStock) return false;
      if (stockFilter === 'out-of-stock' && product.inStock) return false;
      const effectivePrice = product.salePrice && Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price || 0)
        ? Number(product.salePrice)
        : Number(product.price || 0);
      if (minPrice !== '' && effectivePrice < Number(minPrice)) return false;
      if (maxPrice !== '' && effectivePrice > Number(maxPrice)) return false;
      return true;
    });
  }, [products, searchTerm, stockFilter, minPrice, maxPrice]);

  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'price') {
        aValue = a.salePrice && 
                 Number(a.salePrice) > 0 && 
                 Number(a.salePrice) < Number(a.price || 0)
          ? Number(a.salePrice || 0) 
          : Number(a.price || 0);
        bValue = b.salePrice && 
                 Number(b.salePrice) > 0 && 
                 Number(b.salePrice) < Number(b.price || 0)
          ? Number(b.salePrice || 0) 
          : Number(b.price || 0);
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    const startIndex = (page - 1) * productsPerPage;
    return sorted.slice(startIndex, startIndex + productsPerPage);
  }, [filteredProducts, sortBy, sortOrder, page]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const handleAddToCart = async (product) => {
    const requiresCustomization = product.imageRequired > 0 || product.textRequired;
    const productId = product.docId || product.id;
    if (requiresCustomization) {
      navigate(`/products/${productId}`);
      return;
    }
    if (!currentUser) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    const price = product.salePrice &&
      Number(product.salePrice) > 0 &&
      Number(product.salePrice) < Number(product.price || 0)
      ? Number(product.salePrice || 0)
      : Number(product.price || 0);
    try {
      await addToCart(productId, 1, { overridePrice: price });
    } catch (e) {
      console.error('Add to cart failed', e);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategory('');
    setSortBy('name');
    setSortOrder('asc');
    setPage(1);
    setStockFilter('');
    setMinPrice('');
    setMaxPrice('');
  };

  return (
    <>
      <SEOHead
        title="Custom Products - Photo Frames, Lamps, Keychains & More | The Random Store"
        description="Shop custom photo frames, personalized lamps, custom keychains, action figures and unique home decor products. Fast delivery across India. Custom printing services available."
        keywords="custom products, custom photo frame, custom lamps, personalized keychains, custom name keychains, lithophane lamps, action figures, custom home decor, unique gifts, custom printing, products online india"
        url="https://therandomstores.com/products"
      />
      <Box sx={{ backgroundColor: COLORS.background.light, minHeight: '100vh' }}>
        {/* Header Section */}
        <Box sx={{ backgroundColor: COLORS.background.paper, py: 4, mb: 3, borderBottom: `1px solid ${COLORS.border.light}` }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight={700}
              color={COLORS.text.primary}
              sx={{ textAlign: 'left' }}
            >
              Products
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Mobile Filter Toggle Button */}
          {isMobile && (
            <Box mb={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                endIcon={mobileFiltersOpen ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  backgroundColor: COLORS.background.paper,
                  borderColor: COLORS.border.light,
                  color: COLORS.text.primary,
                  '&:hover': {
                    backgroundColor: COLORS.background.light,
                    borderColor: COLORS.border.main,
                  }
                }}
              >
                Filters & Search
              </Button>
            </Box>
          )}

          {/* Mobile Collapsible Filters */}
          {isMobile && (
            <Collapse in={mobileFiltersOpen}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3,
                  backgroundColor: COLORS.background.paper,
                  border: `1px solid ${COLORS.border.light}`,
                  borderRadius: 2,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6" fontWeight={600} color={COLORS.text.primary}>
                    Filters
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setMobileFiltersOpen(false)}
                    sx={{ color: COLORS.text.secondary }}
                  >
                    <ExpandLess />
                  </IconButton>
                </Box>
                
                {/* Mobile Filter Content */}
                <Box>
                  {/* Search Filter */}
                  <Box mb={3}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search products"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: COLORS.text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Box>

                  {/* Categories Filter */}
                  <Box mb={3}>
                    <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                      Categories
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Stock Filter */}
                  <Box mb={3}>
                    <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                      Stock
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={stockFilter}
                        onChange={(e) => { setStockFilter(e.target.value); setPage(1); }}
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">All Stock</MenuItem>
                        <MenuItem value="in-stock">In Stock</MenuItem>
                        <MenuItem value="out-of-stock">Out of Stock</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Price Range */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                      Price Range
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <TextField
                        size="small"
                        placeholder="Min"
                        type="number"
                        value={minPrice}
                        onChange={(e)=> { setMinPrice(e.target.value); setPage(1); }}
                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                      <Typography color={COLORS.text.secondary}>-</Typography>
                      <TextField
                        size="small"
                        placeholder="Max"
                        type="number"
                        value={maxPrice}
                        onChange={(e)=> { setMaxPrice(e.target.value); setPage(1); }}
                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Collapse>
          )}

          <Grid container spacing={3}>
            {/* Desktop Filters Sidebar */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    backgroundColor: COLORS.background.paper,
                    border: `1px solid ${COLORS.border.light}`,
                    borderRadius: 2,
                    height: 'fit-content',
                    position: 'sticky',
                    top: 20
                  }}
                >
                  <Typography variant="h6" fontWeight={600} mb={3} color={COLORS.text.primary}>
                    Filters
                  </Typography>
                  
                  {/* Desktop Filter Content */}
                  <Box>
                    {/* Search Filter */}
                    <Box mb={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search products"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: COLORS.text.secondary }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Box>

                    {/* Categories Filter */}
                    <Box mb={3}>
                      <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                        Categories
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          displayEmpty
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">All Categories</MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Stock Filter */}
                    <Box mb={3}>
                      <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                        Stock
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={stockFilter}
                          onChange={(e) => { setStockFilter(e.target.value); setPage(1); }}
                          displayEmpty
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">All Stock</MenuItem>
                          <MenuItem value="in-stock">In Stock</MenuItem>
                          <MenuItem value="out-of-stock">Out of Stock</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Price Range */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} mb={2} color={COLORS.text.primary}>
                        Price Range
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          placeholder="Min"
                          type="number"
                          value={minPrice}
                          onChange={(e)=> { setMinPrice(e.target.value); setPage(1); }}
                          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        />
                        <Typography color={COLORS.text.secondary}>-</Typography>
                        <TextField
                          size="small"
                          placeholder="Max"
                          type="number"
                          value={maxPrice}
                          onChange={(e)=> { setMaxPrice(e.target.value); setPage(1); }}
                          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Main Content Area */}
            <Grid item xs={12} md={isMobile ? 12 : 9}>
              {/* Sorting Controls */}
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mb={3} flexWrap="wrap">
                {/* Sort By */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color={COLORS.text.secondary} sx={{ whiteSpace: 'nowrap' }}>
                    Sort by:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="price">Price</MenuItem>
                      <MenuItem value="createdAt">Date Added</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Order */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Products Grid */}
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={40} />
                </Box>
              ) : error ? (
                <Box textAlign="center" py={8}>
                  <Typography color="error" variant="h6">
                    {error}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {sortedProducts.map((product) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          border: `1px solid ${COLORS.border.light}`,
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 25px ${withOpacity(COLORS.primary.main, 0.15)}`,
                            borderColor: COLORS.border.main,
                          }
                        }}
                        onClick={() => navigate(`/products/${product.docId || product.id}`)}
                      >
                        <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
                          <LazyImage
                            src={product.imageUrls?.[0] || ''}
                            alt={product.imageAlts?.[0] || product.name || 'Product image'}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {product.salePrice && Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price || 0) && (
                            <Chip
                              label={`${Math.round(((Number(product.price || 0) - Number(product.salePrice)) / Number(product.price || 0)) * 100)}% OFF`}
                              size="small"
                              color="error"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                          {!product.inStock && (
                            <Chip
                              label="Out of Stock"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: COLORS.error.main,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                        
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Typography 
                            variant="h6" 
                            fontWeight={600}
                            color={COLORS.text.primary}
                            sx={{ 
                              mb: 1,
                              fontSize: '1rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              '-webkit-line-clamp': 2,
                              '-webkit-box-orient': 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {product.name}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color={COLORS.text.secondary}
                            sx={{ 
                              mb: 2,
                              display: '-webkit-box',
                              '-webkit-line-clamp': 2,
                              '-webkit-box-orient': 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4
                            }}
                          >
                            {product.shortDescription || product.description}
                          </Typography>
                          
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box>
                              {product.salePrice && Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price || 0) ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight={700}
                                    color={COLORS.primary.main}
                                  >
                                    ₹{Number(product.salePrice || 0).toLocaleString()}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    color={COLORS.text.secondary}
                                    sx={{ textDecoration: 'line-through' }}
                                  >
                                    ₹{Number(product.price || 0).toLocaleString()}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography 
                                  variant="h6" 
                                  fontWeight={700}
                                  color={COLORS.primary.main}
                                >
                                  ₹{Number(product.price || 0).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                            
                            {product.averageRating > 0 && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2" color={COLORS.text.primary} fontWeight={600}>
                                  {product.averageRating.toFixed(1)}
                                </Typography>
                                <Typography variant="body2" color={COLORS.warning.main}>
                                  ★
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(product);
                            }}
                            disabled={!product.inStock}
                            sx={{
                              py: 1.2,
                              borderRadius: 2,
                              fontWeight: 600,
                              backgroundColor: COLORS.primary.main,
                              '&:hover': {
                                backgroundColor: COLORS.primary.dark,
                              },
                              '&:disabled': {
                                backgroundColor: COLORS.text.disabled,
                                color: COLORS.text.secondary,
                              }
                            }}
                          >
                            {!product.inStock ? 'Out of Stock' : (product.imageRequired > 0 || product.textRequired) ? 'Customize' : 'Add to Cart'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={6}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Products;